import {
  SET_SANTE_STEP,
  SET_SANTE_PHASE,
  SET_SANTE_SUBMISSION_REF,
  SET_SANTE_ACTIVE_AFFILIATION,
  SET_SANTE_ADHERENT_FIELD,
  SET_SANTE_AFFILIATION_FIELD,
  TOGGLE_SANTE_AFFLICTION,
  SET_SANTE_AFFLICTION_AUTRE,
  ADD_SANTE_MEDICATION,
  REMOVE_SANTE_MEDICATION,
  ADD_SANTE_AFFILIATION,
  REMOVE_SANTE_AFFILIATION,
  SET_SANTE_DECLARATION_NAME,
  SET_SANTE_DECLARATION_ACCEPTED,
  SET_SANTE_CONSENT_DATA,
  SET_SANTE_FILE,
  SET_SANTE_ERRORS,
  SET_SANTE_ERROR,
  SET_SANTE_LOADING,
  RESET_SANTE,
} from "./mutation-types";
import { computeIndividualPrime } from "@/constants/sante-config";
import service from "@/service";
import config from "@/config";
import axios from "axios";

const $service = service(axios, config);

/** Validation d'une personne. `requirePhone` = true pour l'adhérent. */
function validatePerson(p, requirePhone) {
  const e = {};
  if (!p.lastName || !p.lastName.trim()) e.lastName = "Nom requis.";
  if (!p.firstName || !p.firstName.trim()) e.firstName = "Prénoms requis.";
  if (!p.birthDate) e.birthDate = "Date de naissance requise.";
  if (!p.sex) e.sex = "Sexe requis.";
  if (requirePhone && (!p.phoneNumber || !p.phoneNumber.trim())) {
    e.phone = "Téléphone requis.";
  }
  if (
    p.email &&
    p.email.trim() &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)
  ) {
    e.email = "Email invalide.";
  }
  // Éligibilité âge
  const { eligible, age } = computeIndividualPrime(p);
  if (!eligible) {
    e.birthDate =
      age !== null && age > 70
        ? "Âge non éligible (> 70 ans)."
        : e.birthDate || "Date de naissance invalide.";
  }
  // "Autre" affection => précision requise
  if (
    (p.afflictionCodes || []).includes("AUTRE") &&
    !(p.afflictionAutre || "").trim()
  ) {
    e.afflictionAutre = "Précisez l'affection « Autre ».";
  }
  return e;
}

export default {
  applySanteSetStep: ({ commit }, step) => commit(SET_SANTE_STEP, step),
  applySanteSetPhase: ({ commit }, phase) => commit(SET_SANTE_PHASE, phase),

  applySanteSetActiveAffiliation: ({ commit }, i) =>
    commit(SET_SANTE_ACTIVE_AFFILIATION, i),

  // ── Champs identité ──
  applySanteSetAdherentField: ({ commit }, { key, value }) =>
    commit(SET_SANTE_ADHERENT_FIELD, { key, value }),
  applySanteSetAffiliationField: ({ commit }, { index, key, value }) =>
    commit(SET_SANTE_AFFILIATION_FIELD, { index, key, value }),

  // ── Questionnaire médical (target = 'adherent' | index affilié) ──
  applySanteToggleAffliction: ({ commit }, { target, code }) =>
    commit(TOGGLE_SANTE_AFFLICTION, { target, code }),
  applySanteSetAfflictionAutre: ({ commit }, { target, value }) =>
    commit(SET_SANTE_AFFLICTION_AUTRE, { target, value }),
  applySanteAddMedication: ({ commit }, { target, value }) =>
    commit(ADD_SANTE_MEDICATION, { target, value }),
  applySanteRemoveMedication: ({ commit }, { target, index }) =>
    commit(REMOVE_SANTE_MEDICATION, { target, index }),

  // ── Affiliés ──
  applySanteAddAffiliation: ({ commit }, type) =>
    commit(ADD_SANTE_AFFILIATION, type),
  applySanteRemoveAffiliation: ({ commit }, index) =>
    commit(REMOVE_SANTE_AFFILIATION, index),

  // ── Déclaration ──
  applySanteSetDeclarationName: ({ commit }, value) =>
    commit(SET_SANTE_DECLARATION_NAME, value),
  applySanteSetDeclarationAccepted: ({ commit }, value) =>
    commit(SET_SANTE_DECLARATION_ACCEPTED, value),
  applySanteSetConsentData: ({ commit }, value) =>
    commit(SET_SANTE_CONSENT_DATA, value),

  // ── Fichiers ──
  applySanteSetFile: ({ commit }, { personId, kind, file }) =>
    commit(SET_SANTE_FILE, { personId, kind, file }),

  // ── Validation d'étape ────────────────────────────────────────────
  applySanteValidateStep({ commit, state }, step) {
    if (step === 0) {
      const p = state.form.adherent;
      const e = {};
      if (!p.birthDate) e.birthDate = "Date de naissance requise.";
      // Éligibilité (réutilise computeIndividualPrime déjà importé)
      const { eligible, age } = computeIndividualPrime(p);
      if (p.birthDate && !eligible) {
        e.birthDate =
          age > 70 ? "Âge non éligible (> 70 ans)." : "Date invalide.";
      }
      commit(SET_SANTE_ERRORS, { section: "adherent", errors: e });
      return Object.keys(e).length === 0;
    }
    // Étape 0 : Adhérent principal (téléphone requis)
    if (step === 1) {
      const files = state.files["adherent"] || {};
      const e = validatePerson(state.form.adherent, true);
      if (!files.photo) e.photo = "Photo requise.";
      if (!files.acte) e.acte = "Acte de naissance requis.";
      commit(SET_SANTE_ERRORS, { section: "adherent", errors: e });
      return Object.keys(e).length === 0;
    }

    // Étape 1 : Affiliés (téléphone NON requis) — 0 affilié est autorisé
    if (step === 2) {
      let ok = true;
      state.form.affiliations.forEach((aff, index) => {
        const files = state.files[aff.id] || {};
        const e = validatePerson(aff, false);
        if (!files.photo) e.photo = "Photo requise.";
        if (!files.acte) e.acte = "Acte de naissance requis.";
        commit(SET_SANTE_ERRORS, { section: "affiliation", index, errors: e });
        if (Object.keys(e).length) ok = false;
      });
      return ok;
    }

    // Étape 3 : Déclaration
    if (step === 3) {
      const f = state.form;
      if (
        !f.declarationName.trim() ||
        !f.declarationAccepted ||
        !f.consentData
      ) {
        commit(SET_SANTE_ERROR, {
          key: "declaration",
          message:
            "Renseignez votre nom, cochez la déclaration et le consentement.",
        });
        return false;
      }
      commit(SET_SANTE_ERROR, { key: "declaration", message: "" });
      return true;
    }
    return true;
  },

  // ── Soumission multipart ──────────────────────────────────────────
  async applySanteSubmit({ commit, state, dispatch, getters }) {
    if (!dispatch("applySanteValidateStep", 2)) return { status: 400 };
    if (getters.santeHasIneligible) {
      commit(SET_SANTE_ERROR, {
        key: "submit",
        message: "Une personne est non éligible (âge > 70 ans).",
      });
      return { status: 400 };
    }

    commit(SET_SANTE_LOADING, { key: "submit", value: true });
    commit(SET_SANTE_ERROR, { key: "submit", message: "" });

    try {
      const fd = new FormData();

      const primeMap = getters.santePrimeByPerson;
      const serializePerson = (p) => {
        const pr = primeMap[p.id] || {};
        return {
          localId: p.id,
          type: p.type,
          lastName: p.lastName,
          firstName: p.firstName,
          birthDate: p.birthDate,
          sex: p.sex,
          phoneDial: p.phoneDial,
          phoneNumber: p.phoneNumber || null,
          email: p.email || null,
          address: p.address || null,
          profession: p.profession || null,
          afflictionCodes: p.afflictionCodes,
          afflictionAutre: p.afflictionAutre || null,
          medications: p.medications,
          // Prime calculée côté front (le back peut recalculer pour vérifier)
          age: pr.age,
          basePrime: pr.base,
          afflictionRate: pr.rate,
          individualPrime: pr.prime,
        };
      };

      const payload = {
        adherent: serializePerson(state.form.adherent),
        affiliations: state.form.affiliations.map(serializePerson),
        totalPrime: getters.santeTotalPrime,
        declaration: {
          fullName: state.form.declarationName.trim(),
          accepted: state.form.declarationAccepted,
          consentData: state.form.consentData,
        },
      };
      fd.append("data", JSON.stringify(payload));

      // Fichiers : champs dynamiques photo_<id> / acte_<id>
      const allPersons = [state.form.adherent, ...state.form.affiliations];
      allPersons.forEach((p) => {
        const f = state.files[p.id] || {};
        if (f.photo) fd.append(`photo_${p.id}`, f.photo, f.photo.name);
        if (f.acte) fd.append(`acte_${p.id}`, f.acte, f.acte.name);
      });

      const result = await $service.submitSanteSubscription(fd);
      const ref =
        result?.data?.reference ||
        result?.data?.ref ||
        result?.data?.data?.reference ||
        "—";

      commit(SET_SANTE_SUBMISSION_REF, ref);
      commit(SET_SANTE_PHASE, "success");
      commit(SET_SANTE_LOADING, { key: "submit", value: false });
      return { status: 201, data: result.data };
    } catch (error) {
      let msg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.code === "ECONNABORTED") {
        msg =
          "La requête a pris trop de temps. Vérifiez votre connexion et la taille des fichiers.";
      } else if (error.response) {
        const { status, data } = error.response;
        if (status === 400) {
          msg = Array.isArray(data?.message)
            ? data.message.join(" · ")
            : data?.message || "Données invalides.";
        } else if (status === 413) {
          msg = "Fichier trop volumineux (max 5 Mo par pièce).";
        } else if (status === 415) {
          msg = "Format non supporté. Utilisez PDF, JPG ou PNG.";
        } else if (status >= 500) {
          msg = "Erreur serveur. Réessayez dans un instant.";
        } else if (data?.message) {
          msg = typeof data.message === "string" ? data.message : msg;
        }
      } else if (error.request) {
        msg = "Impossible de joindre le serveur. Vérifiez votre connexion.";
      }
      commit(SET_SANTE_ERROR, { key: "submit", message: msg });
      commit(SET_SANTE_LOADING, { key: "submit", value: false });
      // eslint-disable-next-line no-console
      console.error("[Santé submit] Erreur :", error);
      return { status: error.response?.status || 500, error: msg };
    }
  },

  applySanteReset: ({ commit }) => commit(RESET_SANTE),
};
